import { useMemo } from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { FamilyDto, PermissionDto } from '@families-link/shared';
import { FamiliesTableHandlers } from '../../components/families-table/families-table.component';
import {
	AllowedFamilyActions,
	FamiliesTableActions,
} from '../../components/families-table/families-table-actions.component';
import { getObjectLanguage } from '../../../_core/components/languages-component';
import { Avatar, Box, Skeleton, Typography } from '@mui/material';
import { tableCellTypographyOverflowStyles } from '../../../_core/styles';
import { mergeSx } from 'merge-sx';
import { compact } from 'lodash';

const helper = createColumnHelper<FamilyDto & { createdAt: string; updatedAt: string }>();

/*
  !IMPORTANT!
  Order in property columnsList define order of columns in table.
 */

export type FamilyColumnName =
	| 'fullName'
	| 'email'
	| 'phoneNumber'
	| 'location'
	| 'preferedLanguage'
	| 'totalEngagements'
	| 'district'
	| 'actions'
	| 'importedRow';

export function useFamiliesTableColumns(
	handlers: FamiliesTableHandlers,
	familyColumnsList: FamilyColumnName[],
	allowedFamilyActions: AllowedFamilyActions[],
	isSorted = true,
	isSkeletonLoader = false
): ColumnDef<FamilyDto, string>[] {
	const { onDelete, onEdit, onView } = handlers;

	const [t] = useTranslation();

	const columns = useMemo(
		() =>
			compact(
				familyColumnsList.map((el: FamilyColumnName) => {
					switch (el) {
						case 'importedRow':
							return helper.accessor('importedRow', {
								header: 'Row',
								enableSorting: isSorted,
								meta: {
									flex: 0.5,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue }) => (
									<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
										{getValue()}
									</Typography>
								),
							}) as ColumnDef<FamilyDto, string>;
						case 'district':
							return helper.accessor('district', {
								header: 'District',
								meta: {
									flex: 1,
								},
								cell: ({ getValue }) => (isSkeletonLoader ? <Skeleton /> : getValue()?.name),
							}) as ColumnDef<FamilyDto, string>;
						case 'fullName':
							return helper.accessor('fullName', {
								header: t('families.columns.full-name'),
								meta: {
									flex: 1,
									useCustomOverflowContainer: true,
								},
								enableSorting: isSorted,
								cell: ({ getValue, row }) => {
									const imageUrl = row.original.imageUrl;
									const fullName = getValue();

									return isSkeletonLoader ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
											<Skeleton variant="circular" width={24} height={24} />
											<Skeleton width="60%" />
										</Box>
									) : (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
											<Avatar src={imageUrl} alt={fullName} style={{ width: 24, height: 24, borderRadius: '6px' }} />
											<Typography
												data-overflow="true"
												sx={mergeSx({ fontWeight: 500 }, tableCellTypographyOverflowStyles)}
											>
												{fullName}
											</Typography>
										</Box>
									);
								},
							}) as ColumnDef<FamilyDto, string>;
						case 'email':
							return helper.accessor('email', {
								header: t('families.columns.email'),
								meta: {
									flex: 1,
								},
								enableSorting: isSorted,
								cell: ({ getValue }) => isSkeletonLoader ? <Skeleton /> : getValue(),
							}) as ColumnDef<FamilyDto, string>;
						case 'phoneNumber':
							return helper.accessor('phoneNumber', {
								header: t('families.columns.phone-number'),
								meta: {
									flex: 1,
								},
								enableSorting: isSorted,
								cell: ({ getValue, row }) => {
									const phoneNumber = getValue();

									return isSkeletonLoader ? (
										<Skeleton />
									) : (
										`${phoneNumber.slice(0, 2)} (${phoneNumber.slice(2, 5)}) ${phoneNumber.slice(
											5,
											8
										)}-${phoneNumber.slice(8, 12)}`
									);
								},
							}) as ColumnDef<FamilyDto, string>;
						case 'location':
							return helper.accessor('location', {
								header: t('families.columns.location'),
								meta: {
									flex: 1,
								},
								enableSorting: isSorted,
								cell: ({ getValue }) => isSkeletonLoader ? <Skeleton /> : getValue(),
							}) as ColumnDef<FamilyDto, string>;
						case 'preferedLanguage':
							return helper.accessor('preferedLanguage', {
								header: t('families.columns.prefered-language'),
								meta: {
									flex: 1,
								},
								enableSorting: isSorted,
								cell: (info) => {
									const languages = info.getValue();
									const options = languages.map(getObjectLanguage);

									return isSkeletonLoader ? (
										<Skeleton />
									) : (
										options.map((option) => (option?.name ? option.name : '')).join(', ')
									);
								},
							}) as ColumnDef<FamilyDto, string>;
						case 'totalEngagements':
							return helper.accessor('totalEngagements', {
								header: 'Total Engagements',
								meta: {
									flex: 1,
								},
								enableSorting: false,
								cell: ({ getValue }) => isSkeletonLoader ? <Skeleton /> : getValue(),
							}) as ColumnDef<FamilyDto>;

						case 'actions':
							return helper.display({
								id: 'actions',
								cell: (props) => (
									<FamiliesTableActions
										family={props.row.original}
										allowedFamilyActions={allowedFamilyActions}
										onDelete={onDelete}
										onEdit={onEdit}
										onView={onView}
									/>
								),
							}) as ColumnDef<FamilyDto>;

						default:
							return undefined;
					}
				})
			),
		[t, onDelete, onEdit]
	);

	return columns;
}
